// components/IslandMap2.tsx - Updated with selectedLocation prop
import { useState, useEffect, useRef } from 'react';
import { FaCompass, FaExpand, FaCompress, FaLocationDot } from 'react-icons/fa6';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Location {
  lat: number;
  lng: number;
  name: string;
  description?: string;
}

interface IslandMap2Props {
  center: Location;
  locations?: Location[];
  zoom?: number;
  height?: string;
  className?: string;
  onLocationClick?: (location: Location) => void;
  selectedLocation?: Location | null; // New prop
}

// Component to handle map view changes
function MapController({ center, zoom, selectedLocation }: { center: Location; zoom: number; selectedLocation?: Location | null }) {
  const map = useMap();

  useEffect(() => {
    if (selectedLocation) {
      // Fly to selected location with animation
      map.flyTo([selectedLocation.lat, selectedLocation.lng], 17, {
        duration: 1.5,
        easeLinearity: 0.25,
      });
    } else {
      // Fly back to center
      map.flyTo([center.lat, center.lng], zoom, {
        duration: 1.5,
        easeLinearity: 0.25,
      });
    }
  }, [selectedLocation, center, zoom, map]);

  return null;
}

export default function IslandMap2({
  center,
  locations = [],
  zoom = 13,
  height = '400px',
  className = '',
  onLocationClick,
  selectedLocation,
}: IslandMap2Props) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMapLoaded(true);
  }, []);

  const toggleFullscreen = () => {
    if (!mapContainerRef.current) return;

    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Custom marker icons
  const createMarkerIcon = (color: string, label?: string, isSelected?: boolean) => {
    const size = isSelected ? 44 : 32;
    const borderWidth = isSelected ? 4 : 3;
    const shadow = isSelected ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 8px rgba(0,0,0,0.2)';
    
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: ${shadow};
        border: ${borderWidth}px solid white;
        transition: all 0.3s ease;
        ${isSelected ? 'animation: pulse 1.5s ease-in-out infinite;' : ''}
      ">
        <span style="
          transform: rotate(45deg);
          color: white;
          font-weight: bold;
          font-size: ${isSelected ? '16px' : '12px'};
        ">${label || '📍'}</span>
      </div>
      <style>
        @keyframes pulse {
          0% { transform: rotate(-45deg) scale(1); }
          50% { transform: rotate(-45deg) scale(1.1); }
          100% { transform: rotate(-45deg) scale(1); }
        }
      </style>`,
      iconSize: [size, size],
      iconAnchor: [size/2, size],
    });
  };

  // Check if a location is selected
  const isLocationSelected = (loc: Location) => {
    return selectedLocation && 
           loc.lat === selectedLocation.lat && 
           loc.lng === selectedLocation.lng;
  };

  return (
    <div
      ref={mapContainerRef}
      className={`relative rounded-2xl overflow-hidden shadow-xl bg-gray-100 ${className} ${
        isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''
      }`}
      style={{ height: isFullscreen ? '100vh' : height }}
    >
      {mapLoaded ? (
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={zoom}
          className="w-full h-full"
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
          />
          <ZoomControl position="bottomright" />

          {/* Map Controller for flying to selected location */}
          <MapController center={center} zoom={zoom} selectedLocation={selectedLocation} />

          {/* Center Marker */}
          <Marker
            position={[center.lat, center.lng]}
            icon={createMarkerIcon('#EF4444', 'C')}
          >
            <Popup>
              <div className="p-2">
                <h4 className="font-primary font-bold text-gray-800">{center.name}</h4>
                <p className="font-secondary text-xs text-gray-600">Island Center</p>
              </div>
            </Popup>
          </Marker>

          {/* Location Markers */}
          {locations.map((loc, index) => {
            const isSelected = isLocationSelected(loc);
            const label = String.fromCharCode(65 + index);
            
            return (
              <Marker
                key={index}
                position={[loc.lat, loc.lng]}
                icon={createMarkerIcon(
                  isSelected ? '#8B5CF6' : '#3B82F6',
                  label,
                  isSelected
                )}
                eventHandlers={{
                  click: () => onLocationClick?.(loc),
                }}
              >
                <Popup>
                  <div className="p-2 max-w-[200px]">
                    <h4 className="font-primary font-bold text-gray-800">{loc.name}</h4>
                    <p className="font-secondary text-xs text-gray-600 mt-1">{loc.description}</p>
                    <button
                      onClick={() => onLocationClick?.(loc)}
                      className="mt-2 text-blue-600 hover:text-blue-700 font-secondary text-xs font-medium"
                    >
                      View Details →
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <p className="font-secondary text-gray-500">Loading map...</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
        <button
          onClick={toggleFullscreen}
          className="bg-white/90 backdrop-blur-sm hover:bg-white p-2 rounded-lg shadow-lg transition-all duration-200 hover:scale-110"
          aria-label="Toggle fullscreen"
        >
          {isFullscreen ? (
            <FaCompress className="text-gray-700 text-lg" />
          ) : (
            <FaExpand className="text-gray-700 text-lg" />
          )}
        </button>
      </div>

      {/* Map Legend */}
      <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-3 z-10">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span className="font-secondary text-xs text-gray-700">Island Center</span>
          </div>
          {locations.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500"></div>
              <span className="font-secondary text-xs text-gray-700">Popular Places</span>
            </div>
          )}
          {selectedLocation && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-purple-500"></div>
              <span className="font-secondary text-xs text-gray-700">Selected</span>
            </div>
          )}
        </div>
      </div>

      {/* Location Info */}
      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-3 max-w-[200px] z-10">
        <div className="flex items-center gap-2">
          <FaLocationDot className="text-red-500 text-sm" />
          <p className="font-secondary text-xs text-gray-700 truncate">
            {selectedLocation ? selectedLocation.name : center.name}
          </p>
        </div>
        <p className="font-secondary text-xs text-gray-400 mt-1">
          {selectedLocation 
            ? `${selectedLocation.lat.toFixed(4)}°N, ${selectedLocation.lng.toFixed(4)}°E`
            : `${center.lat.toFixed(4)}°N, ${center.lng.toFixed(4)}°E`
          }
        </p>
        {selectedLocation && (
          <button
            onClick={() => onLocationClick?.(center)}
            className="mt-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            View Center
          </button>
        )}
      </div>

      {/* Location Markers List */}
      {locations.length > 0 && (
        <div className="absolute bottom-4 right-4 max-w-[200px] bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-3 max-h-[150px] overflow-y-auto z-10">
          <p className="font-secondary text-xs font-semibold text-gray-700 mb-2">📍 Popular Places</p>
          {locations.map((loc, index) => {
            const isSelected = isLocationSelected(loc);
            return (
              <button
                key={index}
                onClick={() => onLocationClick?.(loc)}
                className={`flex items-center gap-2 w-full text-left p-1 rounded transition-colors duration-200 ${
                  isSelected ? 'bg-purple-100 text-purple-700' : 'hover:bg-blue-50'
                }`}
              >
                <span className={`font-secondary text-xs font-bold ${
                  isSelected ? 'text-purple-600' : 'text-blue-600'
                } bg-blue-50 w-5 h-5 rounded-full flex items-center justify-center`}>
                  {String.fromCharCode(65 + index)}
                </span>
                <span className={`font-secondary text-xs truncate flex-1 ${
                  isSelected ? 'text-purple-700 font-medium' : 'text-gray-700'
                }`}>
                  {loc.name}
                </span>
                {isSelected && (
                  <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}